import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { WorkerShell } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n";
import { compressImage } from "@/lib/compress";
import { useGeoPermission, requestGeoPermission } from "@/hooks/useGeoPermission";
import { PartsStatusPicker } from "@/pages/PartsQueue";
import WorkerPhotoLightbox from "@/components/WorkerPhotoLightbox";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { buildBeforeAfterComparison } from "@/lib/buildBeforeAfterComparison";
import OfflineQueuePill from "@/components/OfflineQueuePill";
import { tryLiveOrQueue, makePhotoUpload } from "@/lib/offlineQueue";
import LegalProtocolPanel from "@/components/LegalProtocolPanel";

// Haversine distance in meters. Used by auto clock-in + geofence exit.
function haversineMeters(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
  const R = 6371000;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Worker Job View v4 — phone-optimized single-page tech UI.
 * Approved mockup: /public/mockups/job-view-v4.html
 * Fallback: append ?v=classic to fall back to the legacy JobDetail.jsx.
 *
 * Design goals:
 * - One hero + a single stack card of 5 workflow rows (On-the-way / Timer /
 *   Photos / Receipt / Notes). Everything else is folded into "More" or an
 *   admin-only ⚙ drawer so techs see less, do more.
 * - Same backend, no new endpoints.
 */
export default function WorkerJobViewV4() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const geoState = useGeoPermission();
  const gpsBlocked = geoState !== "granted" && user?.role !== "admin";
  const { t } = useT();
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesState, setNotesState] = useState("idle"); // idle|saving|saved
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [comparePair, setComparePair] = useState({ beforeIdx: 0, afterIdx: 0 });
  const notesTimer = useRef(null);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const receiptRef = useRef(null);
  const [tick, setTick] = useState(0); // repaint clock every 30s

  const load = () => api.get(`/jobs/${id}`).then(r => {
    setJob(r.data);
    if (!notes) setNotes(r.data.work_performed_polished || r.data.work_performed || "");
  });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { const iv = setInterval(() => setTick(x => x + 1), 30000); return () => clearInterval(iv); }, []);

  // Auto-resume live GPS streaming when tech is en-route but not clocked in yet.
  useEffect(() => {
    if (!job) return;
    const active = !!job.en_route_at && !job.clock_in_at && job.status !== "completed";
    if (active) startLocationWatch(); else stopLocationWatch();
    return () => stopLocationWatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.en_route_at, job?.clock_in_at, job?.status]);

  // ─── AUTO CLOCK-IN ON ARRIVAL ────────────────────────────────────────
  // Watches live GPS while the tech is en-route (or just viewing the job)
  // and hasn't clocked in yet. When they're inside 120 m of the property
  // for 30 continuous seconds, we fire clock-in automatically — but with
  // a visible 3-2-1 countdown they can cancel if they aren't actually
  // starting work (e.g. dropped off a helper). Guards:
  //  · fires once per session (autoClockRef)
  //  · won't fire on completed / non-mine jobs
  //  · won't fire if geocoded coords missing
  const [autoCountdown, setAutoCountdown] = useState(0);
  const autoClockRef = useRef({ inside: false, sinceMs: 0, fired: false, canceled: false });
  useEffect(() => {
    if (!job || !job.geocoded) return;
    if (job.clock_in_at || job.clock_out_at) return;
    if (job.status === "completed") return;
    const uid = String(user?.id || user?._id || "");
    if (job.assigned_to !== uid && job.helper_id !== uid) return;
    if (!navigator.geolocation) return;
    autoClockRef.current = { inside: false, sinceMs: 0, fired: false, canceled: false };
    let countdownIv = null;
    const tick = (pos) => {
      const st = autoClockRef.current;
      if (st.fired || st.canceled) return;
      const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, job.geocoded.lat, job.geocoded.lng);
      if (d > 140) {
        // Left the geofence buffer — reset counter (12m hysteresis over 120m so a jittery GPS doesn't restart every ping)
        st.inside = false; st.sinceMs = 0; setAutoCountdown(0);
        if (countdownIv) { clearInterval(countdownIv); countdownIv = null; }
        return;
      }
      if (d > 120) return;  // Between 120-140m = "waiting" band, don't reset but don't advance
      // We're inside 120m — start / continue the 30s dwell timer.
      if (!st.inside) { st.inside = true; st.sinceMs = Date.now(); }
      const dwellSec = (Date.now() - st.sinceMs) / 1000;
      if (dwellSec >= 30 && !st.fired && !countdownIv) {
        // 30s satisfied — start the visible 3s countdown they can cancel.
        setAutoCountdown(3);
        countdownIv = setInterval(() => {
          setAutoCountdown(v => {
            if (autoClockRef.current.canceled) { clearInterval(countdownIv); countdownIv = null; return 0; }
            if (v <= 1) {
              clearInterval(countdownIv); countdownIv = null;
              autoClockRef.current.fired = true;
              // Silent clock-in — same endpoint, GPS already validated
              (async () => {
                try {
                  const r = await api.post(`/jobs/${id}/clock-in`, {
                    lat: pos.coords.latitude, lng: pos.coords.longitude,
                  });
                  setJob(r.data);
                  toast.success("📍 Auto clocked-in — you've arrived");
                  stopLocationWatch();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto clock-in failed — tap Clock In");
                }
              })();
              return 0;
            }
            return v - 1;
          });
        }, 1000);
      }
    };
    const opts = { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 };
    const watchId = navigator.geolocation.watchPosition(tick, () => {}, opts);
    return () => {
      navigator.geolocation.clearWatch(watchId);
      if (countdownIv) clearInterval(countdownIv);
      setAutoCountdown(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.geocoded?.lat, job?.geocoded?.lng, job?.clock_in_at, job?.status]);

  const cancelAutoClockIn = () => {
    autoClockRef.current.canceled = true;
    setAutoCountdown(0);
    toast("Auto clock-in canceled — tap Clock In when ready");
  };

  // ─── GEOFENCE EXIT PROMPT ────────────────────────────────────────────
  // While clocked in, watch GPS. If they drift >250 m from the site for
  // 30 s AND we've seen them on-site since clock-in, pop the "What's
  // next?" chooser: Job Done · Material Run · Move to Next WO · Clock Out.
  // Re-prompts every 15 min if ignored.
  const [exitPrompt, setExitPrompt] = useState(false);
  const wasOnSiteRef = useRef(false);
  const driftedSinceRef = useRef(0);
  const exitCooldownUntilRef = useRef(0);
  useEffect(() => {
    if (!job || !job.geocoded) return;
    if (!job.clock_in_at || job.clock_out_at) return;
    if (job.status === "completed") return;
    if (!navigator.geolocation) return;
    wasOnSiteRef.current = false;
    driftedSinceRef.current = 0;
    const tick = (pos) => {
      const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, job.geocoded.lat, job.geocoded.lng);
      if (d <= 120) {
        wasOnSiteRef.current = true;
        driftedSinceRef.current = 0;
        exitCooldownUntilRef.current = 0;
        return;
      }
      if (!wasOnSiteRef.current) return;   // never on-site yet — don't nag
      if (d < 250) { driftedSinceRef.current = 0; return; }
      if (!driftedSinceRef.current) driftedSinceRef.current = Date.now();
      if (Date.now() - driftedSinceRef.current < 30000) return;
      if (Date.now() < exitCooldownUntilRef.current) return;
      setExitPrompt(true);
    };
    const opts = { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 };
    navigator.geolocation.getCurrentPosition(tick, () => {}, opts);
    const iv = setInterval(() => navigator.geolocation.getCurrentPosition(tick, () => {}, opts), 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.geocoded?.lat, job?.geocoded?.lng, job?.clock_in_at, job?.clock_out_at, job?.status]);

  // ─── EXIT PROMPT ACTIONS ────────────────────────────────────────────
  const dismissExitPromptFor15Min = () => {
    exitCooldownUntilRef.current = Date.now() + 15 * 60 * 1000;
    setExitPrompt(false);
  };
  const exitPromptMoveToNext = async () => {
    setExitPrompt(false);
    try {
      // Ask backend for this tech's next scheduled/pinned WO today.
      const jobs = (await api.get("/jobs")).data || [];
      const uid = String(user?.id || user?._id || "");
      const isMine = j => j.assigned_to === uid || j.helper_id === uid || (j.sweeper_pin && j.sweeper_pin.truck_key === uid);
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = jobs.filter(j => isMine(j)
        && j.id !== id
        && j.status !== "completed"
        && !j.clock_in_at
        && (String(j.scheduled_for || "").startsWith(today) || (j.sweeper_pin && j.sweeper_pin.date === today)));
      upcoming.sort((a, b) => (a.sweeper_pin?.route_order || 999) - (b.sweeper_pin?.route_order || 999));
      const next = upcoming[0];
      if (!next) {
        toast("No more WOs on today's route — clocking out this one.");
        await doClockOut();
        return;
      }
      await doClockOut();  // clock out current
      toast.success(`→ ${next.property_address}${next.unit ? " · " + next.unit : ""}`);
      nav(`/worker/jobs/${next.id}`);
    } catch (e) {
      toast.error("Couldn't jump to next WO");
    }
  };
  const exitPromptMaterialRun = async () => {
    try {
      await api.patch(`/jobs/${id}/eta-state`, { eta_state: "material_run" }).catch(() => {});
      toast.success("On a material run — timer keeps running");
    } catch { /* endpoint may not exist yet — ignore */ }
    dismissExitPromptFor15Min();
  };
  // "🥪 Lunch" only surfaces in the exit chooser between 11:00–14:00 local
  // — otherwise it's rare and just clutters the modal. Hits the existing
  // /lunch-start endpoint (pauses the timer).
  const isLunchHour = () => {
    const h = new Date().getHours();
    return h >= 11 && h < 14;
  };
  const exitPromptLunch = async () => {
    setExitPrompt(false);
    try {
      const r = await api.post(`/jobs/${id}/lunch-start`);
      setJob(r.data);
      toast.success("🥪 On lunch — timer paused, tap End Lunch when back");
      exitCooldownUntilRef.current = Date.now() + 60 * 60 * 1000;  // silence exit re-prompt during lunch
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start lunch");
    }
  };


  if (!job) {
    return <WorkerShell><div className="p-8 font-bold" data-testid="jv4-loading">{t("job.loading")}</div></WorkerShell>;
  }

  const done = job.status === "completed";
  const isFollowUp = job.status === "follow_up";
  const isPoolJob = !job.assigned_to;
  const isMine = job.assigned_to === user?.id || job.helper_id === user?.id;
  const isAdmin = user?.role === "admin";
  const canWork = isMine || isAdmin;
  const activeOnClock = !!job.clock_in_at && !job.clock_out_at;
  const onLunch = !!job.lunch_start_at;
  // Paused = tech clocked out (auto or manual) but WO still open. Happens when
  // Alejandro grabs a second stop en route — old WO auto-pauses so timer stops
  // running on both. Show a "Resume" pill so he can pick it back up.
  const isPaused = !activeOnClock && !!job.paused_at &&
                     (job.status || "").toLowerCase() === "in_progress";

  const beforeCount = (job.before_photos || []).length;
  const afterCount = (job.after_photos || []).length;
  const receiptCount = (job.receipts || []).length;
  const notesLen = (notes || "").trim().length;
  const notesOk = notesLen >= 30;
  const hasReceipt = receiptCount > 0 || !!job.no_purchase;

  // ── Timer display ─────────────────────────────────────────────────────
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const timerText = (() => {
    if (!job.clock_in_at) return null;
    const start = new Date(job.clock_in_at).getTime();
    const end = job.clock_out_at ? new Date(job.clock_out_at).getTime() : Date.now();
    const mins = Math.max(0, Math.floor((end - start) / 60000));
    const h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })();
  // Re-declare so `tick` triggers repaint
  void tick;

  // ── API actions ───────────────────────────────────────────────────────
  const withGps = (fn) => {
    if (!navigator.geolocation) return fn(null, null);
    navigator.geolocation.getCurrentPosition(
      (p) => fn(p.coords.latitude, p.coords.longitude),
      () => fn(null, null),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  const doEnRoute = async (mins) => {
    try {
      const r = await api.post(`/jobs/${id}/en-route`, { eta_minutes: mins });
      toast.success(r.data.message || "Office notified");
      startLocationWatch();
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ping failed"); }
  };

  // Live GPS streaming — starts when tech taps "On my way" so the admin
  // Live Field pin moves during the drive; stops on clock-out.
  const startLocationWatch = () => {
    if (!navigator.geolocation) return;
    if (window._enrouteWatch != null) return;
    window._enrouteWatch = navigator.geolocation.watchPosition(async (pos) => {
      try {
        await api.patch(`/jobs/${id}/tech-location`, {
          lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
        });
      } catch {}
    }, () => {}, { enableHighAccuracy: true, maximumAge: 15000 });
  };
  const stopLocationWatch = () => {
    if (window._enrouteWatch != null && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(window._enrouteWatch); } catch {}
      window._enrouteWatch = null;
    }
  };

  // Ping office to prepare the next entries — helpful when the crew is
  // burning through today's route and needs the office to line up keys,
  // PTE calls, and lockbox codes for the next stops.
  const doSendMoreWork = async () => {
    const sp = job.sweeper_pin || {};
    if (!sp.truck_key || !sp.date) {
      toast.error("You're not on a scheduled route today — dispatcher hasn't assigned a truck yet.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/jobs/send-more-work", {
        truck_key: sp.truck_key,
        truck_label: sp.truck_label || "",
        date: sp.date,
        note: "From worker field button",
      });
      if (r.data.silenced) {
        toast("Comms silence is on — office wasn't pinged this time.");
      } else if ((r.data.candidates || []).length > 0) {
        toast.success(`📣 Office pinged — ${r.data.candidates.length} nearby WOs suggested. ${r.data.remaining_stops} stop(s) left.`);
      } else {
        toast.success(`📣 Office pinged — ${r.data.remaining_stops} stop(s) left, no nearby unpinned WOs.`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  const doClockIn = () => {
    setBusy(true);
    withGps(async (lat, lng) => {
      try {
        const r = await api.post(`/jobs/${id}/clock-in`, { lat, lng });
        setJob(r.data);
        toast.success(t("toast.clocked_in"));
      } catch (e) {
        const d = e.response?.data?.detail;
        if (e.response?.status === 409 && d?.code === "funding_pending") {
          const ok = window.confirm(`⚠️ ${d.message}\n\nOverride and clock in anyway?`);
          if (ok) {
            try {
              const r2 = await api.post(`/jobs/${id}/clock-in`, { lat, lng, override_funding_gate: true });
              setJob(r2.data); toast.success(t("toast.clocked_in"));
            } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
          }
        } else if (e.response?.status === 422 && (d?.code === "location_off" || d?.code === "location_far")) {
          if (isAdmin && window.confirm(`${d.message}\n\nAdmin: override just this once?`)) {
            try {
              const r2 = await api.post(`/jobs/${id}/clock-in`, { lat, lng, override_location_gate: true });
              setJob(r2.data); toast.success(t("toast.clocked_in"));
            } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
          } else {
            toast.error(d.message || "Location required to clock in.");
          }
        } else {
          toast.error(formatApiErrorDetail(d));
        }
      } finally { setBusy(false); }
    });
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${id}/clock-out`);
      setJob(r.data); toast.success(t("toast.clocked_out"));
      stopLocationWatch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doLunchStart = async () => {
    try {
      const r = await api.post(`/jobs/${id}/lunch-start`);
      setJob(r.data); toast.success("On lunch — enjoy");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start lunch"); }
  };
  const doLunchEnd = async () => {
    try {
      const r = await api.post(`/jobs/${id}/lunch-end`);
      setJob(r.data);
      const last = (r.data.lunch_breaks || []).slice(-1)[0];
      toast.success(last ? `Back on the clock — ${last.minutes} min lunch` : "Back on the clock");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not end lunch"); }
  };

  const doMaterialRun = () => {
    withGps(async (lat, lng) => {
      try {
        const r = await api.post(`/jobs/${id}/material-run`, { kind: "start", lat, lng });
        setJob(r.data); toast.success(t("job.material_run_logged"));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not log material run"); }
    });
  };

  const uploadPhoto = async (file, kind) => {
    const isVideo = file.type?.startsWith("video/");
    const toSend = isVideo ? file : await compressImage(file);
    // Videos are streamed through the axios client because they're big
    // and don't belong in IndexedDB. Everything else tries live first
    // and falls back to the offline queue on network failure so techs
    // shooting in dead-zone basements never lose evidence.
    if (isVideo) {
      const fd = new FormData();
      fd.append("file", toSend);
      fd.append("kind", kind);
      try {
        await api.post(`/jobs/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${kind} video uploaded`);
        await load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
      return;
    }
    const token = localStorage.getItem("access_token") || "";
    try {
      const { queued } = await tryLiveOrQueue(
        ({ token }) => makePhotoUpload({ jobId: id, kind, blob: toSend, filename: file.name, token }),
        token,
      );
      if (queued) {
        toast(`📶 Offline — ${kind} photo queued, will upload when signal returns`);
      } else {
        toast.success(`${kind} photo uploaded`);
        await load();
      }
    } catch (e) {
      toast.error(e?.message || "Upload failed");
    }
  };

  const onPickPhotos = async (files, kind) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    for (const f of arr) await uploadPhoto(f, kind);
  };

  const doNoPurchase = async (clear) => {
    try {
      if (clear) {
        await api.post(`/jobs/${id}/no-purchase`, { clear: true });
      } else {
        const reason = window.prompt("No purchase on this job — why? (e.g. labor only, used truck stock)", "Labor only");
        if (!reason) return;
        await api.post(`/jobs/${id}/no-purchase`, { reason });
      }
      toast.success(clear ? "Cleared" : "Noted — no purchase on this job");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };

  // Auto-save notes 800ms after last keystroke
  const onNotesChange = (v) => {
    setNotes(v);
    setNotesState("saving");
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      try {
        await api.post(`/jobs/${id}/work-performed`, { text: v });
        setNotesState("saved");
        setTimeout(() => setNotesState("idle"), 1500);
      } catch { setNotesState("idle"); }
    }, 800);
  };

  const doComplete = async () => {
    if (!notesOk) { toast.error(`Notes too short (${notesLen}/30)`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${id}/complete`, { status: "completed", notes });
      setJob(r.data);
      toast.success("Job completed", {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { const rr = await api.post(`/jobs/${id}/reopen`); setJob(rr.data); toast.success("Reopened"); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not undo"); }
          },
        },
      });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "photo_gate") toast.error(`📸 ${d.message}`, { duration: 8000 });
      else if (d?.code === "receipt_gate") toast.error(`🧾 ${d.message || "Receipt required"}`);
      else if (d?.code === "notes_too_short") toast.error(`📝 ${d.message}`);
      else toast.error(formatApiErrorDetail(d) || "Could not complete");
    } finally { setBusy(false); }
  };

  const doReopen = async () => {
    try {
      const r = await api.post(`/jobs/${id}/reopen`);
      setJob(r.data); toast.success("Reopened — back to work");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reopen"); }
  };

  const doClaim = async () => {
    try {
      const r = await api.post(`/jobs/${id}/claim`);
      setJob(r.data); toast.success("Claimed — it's yours");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Admin drawer toggles
  const toggleHabitability = async () => {
    try {
      const r = await api.post(`/jobs/${id}/toggle-habitability`, { value: !job.habitability });
      setJob(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleLocationGate = async () => {
    try {
      const r = await api.post(`/jobs/${id}/toggle-location-gate`, { disabled: !job.location_gate_disabled });
      setJob(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ── Unlock text for Mark Done ─────────────────────────────────────────
  const unlockReasons = [];
  if (afterCount === 0) unlockReasons.push(t("jv4.mark_done.after_photo"));
  if (!hasReceipt) unlockReasons.push(t("jv4.mark_done.receipt"));
  if (!notesOk) unlockReasons.push(t("jv4.mark_done.notes"));
  const canComplete = canWork && activeOnClock && !done && unlockReasons.length === 0;

  const statusPill = (() => {
    if (done) return { text: t("jv4.status.completed"), color: "text-emerald-600" };
    if (isFollowUp) return { text: t("jv4.status.follow_up"), color: "text-amber-700" };
    if (activeOnClock) return { text: t("jv4.status.in_progress"), color: "text-emerald-600" };
    if (isPoolJob) return { text: t("jv4.status.pool"), color: "text-slate-600" };
    return { text: t("jv4.status.assigned"), color: "text-slate-700" };
  })();

  return (
    <WorkerShell>
      <div className="max-w-[460px] mx-auto pb-32 bg-[#f8fafc] min-h-screen" data-testid="jv4-shell">

        {/* AUTO CLOCK-IN COUNTDOWN — visible for 3s so tech can cancel if wrong. */}
        {autoCountdown > 0 && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 bg-emerald-600 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-pulse"
               data-testid="jv4-auto-clockin-banner">
            <span className="text-2xl">📍</span>
            <div className="flex-1">
              <div className="font-black text-sm uppercase tracking-wider">Auto Clocking In</div>
              <div className="text-xs opacity-90">in {autoCountdown}s — you're at the site</div>
            </div>
            <button onClick={cancelAutoClockIn} data-testid="jv4-auto-clockin-cancel"
              className="h-9 px-3 bg-white/20 hover:bg-white/30 text-white font-black text-xs uppercase tracking-wider rounded-lg">
              Cancel
            </button>
          </div>
        )}

        {/* GEOFENCE EXIT PROMPT — modal chooser when they drive off while clocked in. */}
        {exitPrompt && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
               data-testid="jv4-exit-prompt">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-5 shadow-2xl">
              <div className="text-2xl mb-1">🚗</div>
              <div className="font-display font-black text-lg text-[#0B1F3A]">You've left the site</div>
              <div className="text-sm text-slate-600 mt-1">Still on the clock at WO {job.work_order_number || job.id?.slice(0, 6)}. What's next?</div>
              <div className="mt-4 space-y-2">
                <button onClick={exitPromptMoveToNext} data-testid="jv4-exit-next"
                  className="w-full h-12 bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0B1F3A] font-black uppercase text-sm tracking-wider rounded-xl flex items-center justify-center gap-2">
                  → Move to Next WO
                </button>
                <button onClick={exitPromptMaterialRun} data-testid="jv4-exit-material"
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-sm tracking-wider rounded-xl">
                  🛠 Material Run (keep timer)
                </button>
                {isLunchHour() && (
                  <button onClick={exitPromptLunch} data-testid="jv4-exit-lunch"
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-black uppercase text-sm tracking-wider rounded-xl">
                    🥪 Lunch (pause timer)
                  </button>
                )}
                <button onClick={async () => { setExitPrompt(false); await doClockOut(); }}
                  data-testid="jv4-exit-clockout"
                  className="w-full h-12 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase text-sm tracking-wider rounded-xl">
                  ⏹ Clock Out
                </button>
                <button onClick={dismissExitPromptFor15Min} data-testid="jv4-exit-dismiss"
                  className="w-full h-10 text-slate-500 hover:text-[#0B1F3A] font-bold text-xs uppercase tracking-widest">
                  Not now — ask again in 15 min
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="sticky top-16 z-30 bg-white px-4 py-3 flex items-center gap-3 border-b border-slate-200">
          <button onClick={() => nav("/worker")} data-testid="jv4-back"
            className="text-xl text-[#0B1F3A] font-bold">←</button>
          <div className="flex-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {t("jv4.job")} · WO {job.work_order_number || job.id?.slice(0, 6)}
          </div>
          {isAdmin && (
            <button onClick={() => setDrawerOpen(true)} data-testid="jv4-admin-gear"
              title="Admin"
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-[#0B1F3A]">
              ⚙
            </button>
          )}
        </div>

        {/* Hero */}
        <div className="bg-white border-b border-slate-200">
          <div className="h-32 flex items-end px-5 py-3 relative"
            style={{ background: "linear-gradient(135deg, #0B1F3A 0%, #00C4CC 100%)" }}>
            <span className={`inline-flex items-center gap-1.5 bg-white/95 px-3 py-1 rounded-full text-[11px] font-bold ${statusPill.color}`}
              data-testid="jv4-status-pill">
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"/>
              {statusPill.text}
            </span>
            {timerText && (
              <span className="ml-2 inline-flex items-center gap-1.5 bg-[#00C4CC]/95 text-white px-3 py-1 rounded-full text-[11px] font-bold"
                data-testid="jv4-timer-pill">
                ⏱ {timerText}
              </span>
            )}
          </div>
          <div className="px-5 py-4">
            <div className="text-[26px] font-bold tracking-tight text-[#0B1F3A] leading-tight" data-testid="jv4-address">
              {job.property_address || job.title || "—"}
            </div>
            <div className="text-[15px] text-slate-500 font-medium mt-1">
              {job.unit ? `Unit ${job.unit} · ` : ""}{job.city || ""}{job.state ? `, ${job.state}` : ""}{job.zip ? ` ${job.zip}` : ""}
            </div>
            {(job.assigned_to_name || activeOnClock) && (
              <div className="mt-3 text-[13px] text-slate-500 font-medium">
                {job.assigned_to_name && <>{t("jv4.assigned_to")} <b className="text-[#0B1F3A] font-bold">{job.assigned_to_name}</b></>}
                {activeOnClock && job.clock_in_at && <> · {t("jv4.clocked_in_at", { time: fmtTime(job.clock_in_at) })}</>}
              </div>
            )}
          </div>
        </div>

        {/* Legal Protocol — mandatory checklist + moisture readings for
            leak / flood / mold / water-intrusion WOs. Self-hides on WOs
            without a protocol so it doesn't clutter routine tickets. */}
        <div className="mx-3 mt-3">
          <LegalProtocolPanel jobId={id}/>
        </div>

        {/* Paused banner — auto-clocked-out because tech switched to another
            WO. One-tap Resume clocks them back in. */}
        {isPaused && canWork && (
          <div className="mx-3 mt-3 bg-amber-50 border-2 border-amber-400 rounded-2xl p-4 flex items-center gap-3" data-testid="jv4-paused-banner">
            <div className="text-2xl">⏸️</div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-sm text-amber-900 uppercase tracking-wider">Paused</div>
              <div className="text-[11px] text-amber-800 mt-0.5">
                Auto-clocked out at {fmtTime(job.clock_out_at)} — you switched to another WO. Receipt still due when this one closes.
              </div>
            </div>
            <button onClick={doClockIn} disabled={busy}
              data-testid="jv4-resume-btn"
              className="shrink-0 h-11 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-50">
              ▶ Resume
            </button>
          </div>
        )}

        {/* Pool CTA */}
        {isPoolJob && !isAdmin && (
          <div className="mx-3 mt-3 bg-white rounded-2xl p-5 shadow-sm text-center" data-testid="jv4-pool-cta">
            <div className="font-bold text-[#0B1F3A] mb-2">{t("jv4.pool_hint")}</div>
            <button onClick={doClaim} data-testid="jv4-claim-btn"
              className="w-full h-12 rounded-xl bg-[#00C4CC] hover:bg-[#00a8af] text-white font-bold">
              {t("jv4.claim")}
            </button>
          </div>
        )}

        {/* Workflow stack */}
        {(canWork && !isPoolJob) && (
          <div className="mx-3 mt-3 bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="jv4-stack">

            {/* Row 1 · On the way */}
            <Row
              icon={job.en_route_at ? "✓" : "🚗"}
              tint={job.en_route_at ? "green" : "teal"}
              label={t("jv4.on_the_way")}
              sub={
                job.en_route_at
                  ? (job.en_route_eta_minutes
                      ? t("jv4.on_the_way_sub_sent", { eta: job.en_route_eta_minutes })
                      : t("jv4.on_the_way_sub_sent_no_eta"))
                  : t("jv4.on_the_way_sub_none")
              }
              done={!!job.en_route_at}
              active={!job.en_route_at && !activeOnClock}
              testid="jv4-row-enroute"
            >
              {!job.en_route_at && !done && (
                <div className="grid grid-cols-4 gap-1.5 mt-2.5">
                  {[5, 10, 15, 30].map(m => (
                    <button key={m} onClick={() => doEnRoute(m)}
                      data-testid={`jv4-eta-${m}`}
                      className="h-10 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-sm font-bold text-[#0B1F3A]">
                      {t("jv4.eta_minutes", { n: m })}
                    </button>
                  ))}
                </div>
              )}
            </Row>

            {/* Row 2 · Timer + Lunch + Materials */}
            <Row
              icon="⏱️"
              tint="teal"
              label={
                <span className="flex items-center gap-2">
                  {t("jv4.timer")}
                  {activeOnClock && !onLunch && (
                    <span className="bg-[#00C4CC] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {t("jv4.timer.running")}
                    </span>
                  )}
                  {onLunch && (
                    <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      🍔 {t("jv4.lunch")}
                    </span>
                  )}
                </span>
              }
              sub={
                onLunch
                  ? t("jv4.timer.on_lunch", { time: fmtTime(job.lunch_start_at) })
                  : (activeOnClock && job.clock_in_at
                      ? t("jv4.timer.started_at", { time: fmtTime(job.clock_in_at) })
                      : t("jv4.not_clocked_in"))
              }
              active={activeOnClock}
              testid="jv4-row-timer"
            >
              <div className="flex gap-2 flex-wrap mt-2.5">
                {!activeOnClock && !done && (
                  <button
                    onClick={async () => {
                      if (gpsBlocked) {
                        // One-tap re-prompt if the user has GPS in "prompt"
                        // state. If "denied" or "unsupported" they need to
                        // fix it in browser settings — surface a hint.
                        if (geoState === "prompt") {
                          const next = await requestGeoPermission();
                          if (next === "granted") { doClockIn(); return; }
                        }
                        toast.error(
                          geoState === "denied"
                            ? "GPS is blocked. Tap the 🔒 in your address bar → Allow Location → refresh."
                            : geoState === "unsupported"
                            ? "This browser can't share GPS. Open Field Radar in Chrome or Safari."
                            : "Turn on Location to clock in."
                        );
                        return;
                      }
                      doClockIn();
                    }}
                    disabled={busy}
                    data-testid="jv4-clock-in"
                    title={gpsBlocked ? "Enable Location to clock in" : ""}
                    className={`h-9 px-3 rounded-lg text-white text-xs font-bold disabled:opacity-50 ${
                      gpsBlocked ? "bg-red-500 hover:bg-red-600" : "bg-[#00C4CC] hover:bg-[#00a8af]"
                    }`}>
                    {gpsBlocked ? "📍 Enable GPS" : `▶ ${t("jv4.clock_in")}`}
                  </button>
                )}
                {activeOnClock && !onLunch && (
                  <>
                    <button onClick={doClockOut} disabled={busy} data-testid="jv4-clock-out"
                      className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0B1F3A] text-xs font-bold">
                      ■ {t("jv4.clock_out")}
                    </button>
                    <button onClick={doLunchStart} data-testid="jv4-lunch-start"
                      className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0B1F3A] text-xs font-bold">
                      🍔 {t("jv4.lunch")}
                    </button>
                    <button onClick={doMaterialRun} data-testid="jv4-material-run"
                      className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0B1F3A] text-xs font-bold">
                      🛒 {t("jv4.materials_run")}
                    </button>
                    {job.sweeper_pin?.truck_key && (
                      <button onClick={doSendMoreWork} disabled={busy} data-testid="jv4-send-more-work"
                        className="h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-50"
                        title="Ping office to prep entries for the next stops">
                        📣 Send More Work
                      </button>
                    )}
                  </>
                )}
                {onLunch && (
                  <button onClick={doLunchEnd} data-testid="jv4-lunch-end"
                    className="h-9 px-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold">
                    {t("jv4.end_lunch")}
                  </button>
                )}
              </div>
            </Row>

            {/* Row 3 · Photos */}
            <Row
              icon="📸"
              tint="pink"
              label={t("jv4.photos")}
              sub={
                <span>
                  <span className="text-emerald-600 font-semibold">{beforeCount} {t("jv4.photos.before").toLowerCase()}</span>
                  {" · "}
                  <span className={afterCount === 0 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>
                    {afterCount} {t("jv4.photos.after").toLowerCase()}
                  </span>
                  {!done && afterCount === 0 && ` — ${t("jv4.photos.need_after")}`}
                </span>
              }
              done={afterCount > 0 && !done}
              testid="jv4-row-photos"
            >
              <div className="mt-2"><OfflineQueuePill compact /></div>
              {beforeCount > 0 && afterCount > 0 && (
                <button onClick={() => { setComparePair({ beforeIdx: 0, afterIdx: 0 }); setCompareOpen(true); }}
                  data-testid="jv4-compare-btn"
                  className="mt-2 h-9 px-3 rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1.5 shadow-sm">
                  ⇔ Compare Before / After
                </button>
              )}
              <div className="flex gap-2 mt-2.5 overflow-x-auto pb-1">
                {(() => {
                  const merged = [...(job.before_photos || []), ...(job.after_photos || [])];
                  return merged.slice(0, 6).map((p, i) => (
                    <button key={p.file_id || i} onClick={() => setLightboxIdx(i)} type="button"
                      className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200"
                      data-testid={`jv4-photo-thumb-${i}`}>
                      <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover"/>
                    </button>
                  ));
                })()}
                {!done && (
                  <>
                    <button onClick={() => beforeRef.current?.click()} data-testid="jv4-add-before"
                      className="w-14 h-14 rounded-lg border-2 border-dashed border-[#00C4CC] bg-white text-[#00C4CC] font-bold flex-shrink-0 flex items-center justify-center text-xs">
                      + {t("jv4.photos.before")}
                    </button>
                    <button onClick={() => afterRef.current?.click()} data-testid="jv4-add-after"
                      className={`w-14 h-14 rounded-lg border-2 border-dashed flex-shrink-0 flex items-center justify-center text-xs font-bold
                        ${afterCount === 0 ? "border-red-500 bg-red-50 text-red-600" : "border-[#00C4CC] bg-white text-[#00C4CC]"}`}>
                      + {t("jv4.photos.after")}
                    </button>
                  </>
                )}
              </div>
              <input ref={beforeRef} type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { onPickPhotos(e.target.files, "before"); e.target.value = ""; }}
                data-testid="jv4-before-input"/>
              <input ref={afterRef} type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { onPickPhotos(e.target.files, "after"); e.target.value = ""; }}
                data-testid="jv4-after-input"/>
            </Row>

            {/* Row 4 · Receipt */}
            <Row
              icon="🧾"
              tint={hasReceipt ? "green" : "warn"}
              label={t("jv4.receipt")}
              sub={
                job.no_purchase
                  ? t("jv4.receipt.no_purchase", { reason: job.no_purchase.reason || "" })
                  : receiptCount > 0
                    ? t("jv4.receipt.count", { n: receiptCount })
                    : t("jv4.receipt.none")
              }
              done={hasReceipt}
              testid="jv4-row-receipt"
            >
              {!done && !hasReceipt && (
                <div className="flex gap-2 mt-2.5 flex-wrap">
                  <button onClick={() => receiptRef.current?.click()} data-testid="jv4-add-receipt"
                    className="h-9 px-3 rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] text-white text-xs font-bold">
                    {t("jv4.receipt.add")}
                  </button>
                  <button onClick={() => doNoPurchase(false)} data-testid="jv4-no-purchase"
                    className="h-9 px-3 rounded-lg bg-white text-[#0B1F3A] text-xs font-bold border border-slate-200">
                    {t("jv4.receipt.mark_none")}
                  </button>
                </div>
              )}
              {job.no_purchase && !done && (
                <button onClick={() => doNoPurchase(true)} data-testid="jv4-clear-no-purchase"
                  className="mt-2.5 text-[11px] font-bold text-slate-500 underline">
                  Undo "no purchase"
                </button>
              )}
              <input ref={receiptRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) { uploadPhoto(e.target.files[0], "receipt"); e.target.value = ""; }}}
                data-testid="jv4-receipt-input"/>
            </Row>

            {/* Row 4b · Parts status — worker can flag Waiting / Ordered
                so office and dispatch see it in the Parts Queue. */}
            <Row
              icon="🔧"
              tint="teal"
              label="Parts"
              alignTop
              testid="jv4-row-parts"
            >
              <div className="mt-2" data-testid="jv4-parts-picker-wrapper">
                <PartsStatusPicker
                  job={job}
                  currentStatus={job.parts_status}
                  currentNote={job.parts_note || ""}
                  onSaved={load}
                />
                {job.parts_note && (
                  <div className="mt-2 text-xs text-slate-600 italic">📝 {job.parts_note}</div>
                )}
              </div>
            </Row>

            {/* Row 5 · Notes */}
            <Row
              icon="📝"
              tint="blue"
              label={t("jv4.notes")}
              alignTop
              testid="jv4-row-notes"
            >
              <textarea
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder={t("jv4.notes.placeholder")}
                disabled={done}
                data-testid="jv4-notes-textarea"
                className={`w-full mt-2 h-24 px-3 py-2 rounded-xl border text-sm resize-y bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#00C4CC]
                  ${done ? "opacity-60" : ""} ${notesOk ? "border-emerald-300" : "border-slate-200"}`}
              />
              <div className={`flex items-center justify-between mt-1.5 text-[11px] font-semibold
                ${notesOk ? "text-emerald-600" : notesLen > 0 ? "text-red-600" : "text-slate-500"}`}>
                <span data-testid="jv4-notes-counter">
                  {notesOk
                    ? t("jv4.notes.ok", { n: notesLen })
                    : t("jv4.notes.short", { n: notesLen, more: 30 - notesLen })}
                </span>
                <span className="text-slate-500">
                  {notesState === "saving" ? t("jv4.notes.saving")
                    : notesState === "saved" ? t("jv4.notes.saved")
                    : t("jv4.notes.autosave")}
                </span>
              </div>
            </Row>

          </div>
        )}

        {/* Completed / follow-up state */}
        {done && (
          <div className="mx-3 mt-3 bg-emerald-50 border-2 border-emerald-500 rounded-2xl p-5 text-center" data-testid="jv4-completed-banner">
            <div className="text-4xl mb-2">✅</div>
            <div className="font-bold text-emerald-900 text-lg">{t("jv4.status.completed")}</div>
            {canWork && (
              <button onClick={doReopen} data-testid="jv4-reopen"
                className="mt-3 h-10 px-4 rounded-lg bg-white text-emerald-800 border-2 border-emerald-500 text-sm font-bold">
                ↺ {t("jv4.reopen")}
              </button>
            )}
          </div>
        )}

        {/* More section — nav to full JobDetail for less-used features */}
        <div className="mx-3 mt-3 bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="jv4-more">
          <div className="px-4 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-100">
            {t("jv4.more")}
          </div>
          <MoreItem icon="🔑" label={t("jv4.more.property_notes")} to={`/worker/jobs/${id}?v=classic#property`} testid="jv4-more-notes"/>
          <MoreItem icon="💬" label={t("jv4.more.chat")} to={`/worker/team-chat`} testid="jv4-more-chat"/>
          <MoreItem icon="🕒" label={t("jv4.more.timeline")} to={`/worker/jobs/${id}?v=classic#timeline`} testid="jv4-more-timeline"/>
          <MoreItem icon="💵" label={t("jv4.more.quote")} to={`/worker/jobs/${id}?v=classic#quote`} testid="jv4-more-quote"/>
          <MoreItem icon="↺" label={t("jv4.more.classic")} to={`/worker/jobs/${id}?v=classic`} testid="jv4-more-classic"/>
        </div>

        {/* Sticky Mark Done CTA */}
        {canWork && !isPoolJob && !done && (
          <div className="fixed left-0 right-0 bottom-0 z-20 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] bg-gradient-to-t from-white via-white/95 to-transparent max-w-[460px] mx-auto"
            data-testid="jv4-cta-wrap">
            <button onClick={doComplete} disabled={!canComplete || busy}
              data-testid="jv4-mark-done"
              className={`w-full h-14 rounded-2xl font-bold text-base tracking-wide
                ${canComplete
                  ? "bg-[#00C4CC] hover:bg-[#00a8af] text-white shadow-[0_4px_14px_rgba(0,196,204,0.35)]"
                  : "bg-slate-300 text-white cursor-not-allowed"}`}>
              {t("jv4.mark_done")}
            </button>
            {!canComplete && unlockReasons.length > 0 && (
              <div className="text-center text-[11px] text-slate-500 font-medium mt-1.5" data-testid="jv4-unlock-help">
                {t("jv4.mark_done.unlock", { items: unlockReasons.join(" + ") })}
              </div>
            )}
            {!canComplete && activeOnClock === false && unlockReasons.length === 0 && (
              <div className="text-center text-[11px] text-slate-500 font-medium mt-1.5">
                Clock in to start
              </div>
            )}
          </div>
        )}

        {/* Admin drawer */}
        {isAdmin && drawerOpen && (
          <div className="fixed inset-0 z-50 bg-black/45 flex justify-end" onClick={() => setDrawerOpen(false)} data-testid="jv4-drawer-backdrop">
            <div className="w-[92%] max-w-[400px] h-full bg-white overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="jv4-drawer">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-bold text-[#0B1F3A]">{t("jv4.admin.title")}</h3>
                <button onClick={() => setDrawerOpen(false)} data-testid="jv4-drawer-close"
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-[#0B1F3A]">×</button>
              </div>
              <p className="text-xs text-slate-500 mb-5">{t("jv4.admin.desc")}</p>

              <AdminRow label={t("jv4.admin.habitability")} sub={t("jv4.admin.habitability_sub")}>
                <Toggle on={!!job.habitability} onChange={toggleHabitability} testid="jv4-toggle-habitability"/>
              </AdminRow>
              <AdminRow label={t("jv4.admin.location_gate")} sub={t("jv4.admin.location_gate_sub")}>
                <Toggle on={!job.location_gate_disabled} onChange={toggleLocationGate} testid="jv4-toggle-locationgate"/>
              </AdminRow>
              <AdminRow label={t("jv4.admin.triage_link")} sub={t("jv4.admin.triage_link_sub")}>
                <Link to={`/worker/jobs/${id}?v=classic#triage`} onClick={() => setDrawerOpen(false)}
                  data-testid="jv4-open-triage"
                  className="h-9 px-3 inline-flex items-center rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] text-white text-xs font-bold">
                  {t("jv4.admin.mint")}
                </Link>
              </AdminRow>
              <AdminRow label="Full admin panel" sub="Quote, chargeback, exports, print PDFs — opens classic view">
                <Link to={`/worker/jobs/${id}?v=classic`} onClick={() => setDrawerOpen(false)}
                  data-testid="jv4-open-classic-admin"
                  className="h-9 px-3 inline-flex items-center rounded-lg bg-white text-[#0B1F3A] border border-slate-200 text-xs font-bold">
                  Open
                </Link>
              </AdminRow>
            </div>
          </div>
        )}
      </div>
      {/* Full-screen photo lightbox — CompanyCam parity for techs.
          Opens on thumb tap. Markup / caption / hold-or-tap voice notes
          all live inside. */}
      {lightboxIdx !== null && (() => {
        const merged = [...(job.before_photos || []), ...(job.after_photos || [])];
        const norm = merged.map(p => ({
          file_id: p?.file_id || null,
          path: p?.path || null,
          url: p?.path ? fileUrl(p.path) : (p?.url || ""),
          caption: p?.caption || "",
          caption_by: p?.caption_by || "",
          voice_note: p?.voice_note || null,
          kind: p?.kind || null,
          at: p?.uploaded_at || p?.created_at || null,
        }));
        return (
          <WorkerPhotoLightbox
            photos={norm}
            startIdx={Math.min(lightboxIdx, norm.length - 1)}
            jobId={id}
            canEdit={canWork && !done}
            apiBase={(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}
            onClose={() => setLightboxIdx(null)}
            onMutated={load}
          />
        );
      })()}

      {/* Before/After Compare slider — techs asked for the same
          landlord-wow drag comparison the office already had. */}
      {compareOpen && (
        <BeforeAfterCompare
          jobId={id}
          beforePhotos={job.before_photos || []}
          afterPhotos={job.after_photos || []}
          pair={comparePair}
          onChangePair={setComparePair}
          onClose={() => setCompareOpen(false)}
          onSaved={load}
        />
      )}
    </WorkerShell>
  );
}

// Compact worker-side Before/After compare sheet.
function BeforeAfterCompare({ jobId, beforePhotos, afterPhotos, pair, onChangePair, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const b = beforePhotos[pair.beforeIdx];
  const a = afterPhotos[pair.afterIdx];
  if (!b || !a) return null;
  const saveComparison = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const blob = await buildBeforeAfterComparison(fileUrl(b.path), fileUrl(a.path));
      if (!blob) throw new Error("Couldn't build the image");
      const fd = new FormData();
      fd.append("kind", "after");   // attach to the After lane so it lives with the completed set
      fd.append("file", new File([blob], `compare-${Date.now()}.jpg`, { type: "image/jpeg" }));
      fd.append("caption", "Before / After comparison");
      await api.post(`/jobs/${jobId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comparison saved to WO");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(`Couldn't save: ${e?.response?.data?.detail || e.message || "try again"}`);
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col" data-testid="jv4-compare-sheet"
         onClick={onClose}>
      <div className="p-3 flex items-center gap-2 border-b border-white/10 bg-black/60"
           onClick={(e) => e.stopPropagation()}>
        <div className="text-white font-black uppercase tracking-widest text-sm">Compare</div>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {beforePhotos.length > 1 && (
            <select value={pair.beforeIdx}
              onChange={(e) => onChangePair({ ...pair, beforeIdx: Number(e.target.value) })}
              data-testid="jv4-compare-before-picker"
              className="h-8 px-2 rounded bg-slate-800 text-white text-[11px] font-bold border border-slate-600">
              {beforePhotos.map((_, i) => <option key={i} value={i}>Before #{i+1}</option>)}
            </select>
          )}
          {afterPhotos.length > 1 && (
            <select value={pair.afterIdx}
              onChange={(e) => onChangePair({ ...pair, afterIdx: Number(e.target.value) })}
              data-testid="jv4-compare-after-picker"
              className="h-8 px-2 rounded bg-slate-800 text-white text-[11px] font-bold border border-slate-600">
              {afterPhotos.map((_, i) => <option key={i} value={i}>After #{i+1}</option>)}
            </select>
          )}
          <button onClick={saveComparison} disabled={saving}
            data-testid="jv4-compare-save"
            className="h-8 px-3 rounded bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-60">
            {saving ? "Saving…" : "💾 Save Comparison"}
          </button>
          <button onClick={onClose} data-testid="jv4-compare-close"
            className="h-8 w-8 rounded-full text-white bg-white/10 hover:bg-white/20 flex items-center justify-center">
            ✕
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-3" onClick={(e) => e.stopPropagation()}>
        <BeforeAfterSlider
          before={fileUrl(b.path)}
          after={fileUrl(a.path)}
          className="max-h-full max-w-full"/>
      </div>
      <div className="p-2 flex items-center justify-center text-white/70 text-[10px] font-black uppercase tracking-widest">
        Drag the handle to compare · save to attach a static split image
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────
function Row({ icon, tint = "teal", label, sub, done, active, alignTop, children, testid }) {
  const tints = {
    teal:  "bg-[#E6FAFB] text-[#008a8f]",
    green: "bg-emerald-100 text-emerald-700",
    warn:  "bg-amber-100 text-amber-700",
    blue:  "bg-blue-100 text-blue-800",
    pink:  "bg-pink-100 text-pink-700",
  };
  return (
    <div data-testid={testid}
      className={`relative flex gap-3.5 px-4 py-4 border-b border-slate-100 last:border-0 ${alignTop ? "items-start" : "items-center"}`}>
      {active && <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#00C4CC]"/>}
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${tints[tint] || tints.teal}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-[#0B1F3A]">{label}</div>
        {sub && <div className="text-[12px] text-slate-500 font-medium mt-0.5">{sub}</div>}
        {children}
      </div>
      {done && (
        <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
          ✓
        </div>
      )}
    </div>
  );
}

function MoreItem({ icon, label, to, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-[#0B1F3A] border-b border-slate-100 last:border-0 hover:bg-slate-50">
      <span className="text-lg">{icon}</span>
      <span className="flex-1">{label}</span>
      <span className="text-slate-400">›</span>
    </Link>
  );
}

function AdminRow({ label, sub, children }) {
  return (
    <div className="py-3.5 flex items-center gap-3 border-b border-slate-100">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#0B1F3A]">{label}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ on, onChange, testid }) {
  return (
    <button onClick={onChange} data-testid={testid}
      className={`w-[46px] h-[26px] rounded-full relative transition-colors flex-shrink-0
        ${on ? "bg-[#00C4CC]" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 w-[22px] h-[22px] rounded-full bg-white shadow-sm transition-transform
        ${on ? "translate-x-[22px]" : "translate-x-0.5"}`}/>
    </button>
  );
}
